import React, { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DataTable, DataTableColumn, TableActions } from '@saucelabs/ds-table';
import { Loader } from '@saucelabs/sl-component-loader';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { Typography } from '@saucelabs/ds-typography';
import { IconStatusPassColor, IconStatusFailColor } from '@saucelabs/ds-icons';

import { ValueOf } from '../../../globalTypes';
import constants from '../constants';
import selectors from '../selectors';
import { UploadToSdFile } from '../records';

type RowData = {
  file: UploadToSdFile;
  id: string;
  lastModified: number;
  name: string;
  status: ValueOf<typeof constants.UPLOAD_STATUS>;
};

const UploadToSdCardFilesList: React.FC = () => {
  const { files } = useSelector(selectors.uploadToSdCardSelector);

  const columns: DataTableColumn<RowData>[] = useMemo(() => (
    [
      {
        allowsSorting: true,
        customBodyRender: ((value) => (
          <Typography
            aria-label={value as string}
            className="break-word"
            variant="body"
          >
            {value as string}
          </Typography>
        )),
        customSort: (name1: string, name2: string) => name1.localeCompare(name2),
        field: 'name',
        key: 'name',
        name: 'File Name',
      },
      {
        allowsSorting: true,
        customBodyRender: ((value) => (
          <div
            aria-label={value as string}
            className="pl-0.25"
            role="status"
          >
            <Loader
              fontSize={1.5}
              show={value === constants.UPLOAD_STATUS.UPLOADING}
            >
              {value === constants.UPLOAD_STATUS.UPLOADED && (
                <Tooltip title="Success">
                  <span><IconStatusPassColor className="w-2.5 h-2.5 align-text-bottom" /></span>
                </Tooltip>
              )}
              {value === constants.UPLOAD_STATUS.FAILED && (
                <Tooltip title="Failure">
                  <span><IconStatusFailColor className="w-2.5 h-2.5 align-text-bottom" /></span>
                </Tooltip>

              )}
            </Loader>
          </div>
        )),
        customSort: (status1: string, status2: string) => status2.localeCompare(status1),
        field: 'status',
        key: 'status',
        name: 'Status',
        widthClass: 'w-14',
      },
      {
        allowsSorting: true,
        customBodyRender: ((value, key, row) => (
          <div
            aria-label="uploaded at"
            className="pl-0.25"
          >
            {!value && (
              <Typography variant="body-small">
                <span>foobar</span>
              </Typography>
            )}
            {value && (
              <Tooltip title={row.file.getDatePrettyLabel()}>
                <Typography variant="body">
                  <span>{row.file.getRelativeDatePrettyLabel()}</span>
                </Typography>
              </Tooltip>
            )}
          </div>
        )),
        customSort: (date1: number, date2: number) => (
          `${date1}`.localeCompare(`${date2}`)
        ),
        field: 'lastModified',
        key: 'uploaded',
        name: 'Uploaded',
        widthClass: 'w-24',
      },
    ]
  ), []);

  const rows = useMemo(
    () => (
      files.map((file) => ({
        file,
        id: file.id,
        lastModified: file.lastModified,
        name: file.name,
        status: file.status,
      }))),
    [files],
  );

  return (
    <DataTable
      aria-label="Upload to SD Card Files List"
      columns={columns}
      defaultSort={{
        column: 'uploaded',
        direction: 'descending',
      }}
      rows={rows}
    />
  );
};

UploadToSdCardFilesList.displayName = 'UploadToSdCardFilesList';

export default UploadToSdCardFilesList;
